import * as React from 'react';

import { cn } from '@veraclins-dev/utils';

import { type IconName } from '../icons';

import { type InputVariants, inputVariants } from './utils/variants/input';
import { INPUT_CLASS_OVERRIDES } from './utils/variants/shared-classes';
import { extractStyleProps } from './utils/variants/styles';
import { Box } from './box';
import { Icon, type IconProps } from './icon';

export type InputProps = React.ComponentProps<'input'> &
  InputVariants & {
    leftIcon?: IconProps | IconName;
    rightIcon?: IconProps | IconName;
  };

/**
 * A text input with variant styles and optional left or right icons.
 * @category interactive-elements
 */
function Input({
  className,
  type,
  ref,
  inputSize,
  surface,
  leftIcon,
  rightIcon,
  ...props
}: InputProps) {
  const { styleProps, others } = extractStyleProps(props);
  const hasIcons = leftIcon || rightIcon;

  return !hasIcons ? (
    <input
      ref={ref}
      type={type}
      data-slot="input"
      className={cn(
        inputVariants({ ...styleProps, className, inputSize, surface }),
      )}
      {...others}
    />
  ) : (
    <Box
      data-slot="input-group"
      display="flex"
      items="center"
      gap={2}
      className={cn(
        inputVariants({ ...styleProps, className, inputSize, surface }),
      )}
    >
      {leftIcon && (
        <Icon
          {...(typeof leftIcon === 'string' ? { name: leftIcon } : leftIcon)}
        />
      )}
      <input
        ref={ref}
        type={type}
        data-slot="input"
        className={cn(
          inputVariants({ ...styleProps, className, inputSize }),
          INPUT_CLASS_OVERRIDES,
        )}
        {...others}
      />
      {rightIcon && (
        <Icon
          {...(typeof rightIcon === 'string' ? { name: rightIcon } : rightIcon)}
        />
      )}
    </Box>
  );
}

export { Input };
